import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { kbApi } from '../api/kb';
import { useCompany } from '../contexts/CompanyContext';
import { usePermissions } from '../contexts/PermissionsContext';
import toast from 'react-hot-toast';
import { Save, Eye, EyeOff, Globe, Lock } from 'lucide-react';
import PageHeader from '../components/PageHeader';

export default function ArticleEditor() {
  const { id } = useParams<{ id?: string }>();
  const isEditing = !!id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentCompany } = useCompany();
  const { can } = usePermissions();

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [category, setCategory] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [status, setStatus] = useState<'draft' | 'published' | 'archived'>('draft');
  const [visibility, setVisibility] = useState<'internal' | 'external'>('external');

  const { data: article, isLoading: loadingArticle } = useQuery({
    queryKey: ['kb-article-edit', id],
    queryFn: () => kbApi.getBySlug(id!),
    enabled: isEditing && !!id && !!currentCompany,
  });

  // Seed from the loaded article once — see TicketEditModal: keying this on the
  // object itself let any background refetch overwrite in-progress edits.
  useEffect(() => {
    if (article) {
      setTitle(article.title);
      setContent(article.content);
      setExcerpt(article.excerpt ?? '');
      setCategory(article.category ?? '');
      setTagsInput(article.tags.join(', '));
      setStatus(article.status as any);
      setVisibility(article.visibility as any);
    }
  }, [article?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const createMutation = useMutation({
    mutationFn: () => kbApi.create({
      title,
      content,
      excerpt: excerpt || undefined,
      category: category || undefined,
      tags: tagsInput ? tagsInput.split(',').map(t => t.trim()).filter(Boolean) : [],
      status,
      visibility,
    }),
    onSuccess: (created) => {
      toast.success('Article created');
      queryClient.invalidateQueries({ queryKey: ['kb'] });
      navigate(`/kb/${created.slug}`);
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Failed to create article'),
  });

  const updateMutation = useMutation({
    mutationFn: () => {
      const articleId = (article as any)?.id ?? (article as any)?._id;
      return kbApi.update(articleId, {
        title,
        content,
        excerpt: excerpt || undefined,
        category: category || undefined,
        tags: tagsInput ? tagsInput.split(',').map(t => t.trim()).filter(Boolean) : [],
        status,
        visibility,
      });
    },
    onSuccess: (updated) => {
      toast.success('Article updated');
      queryClient.invalidateQueries({ queryKey: ['kb'] });
      queryClient.invalidateQueries({ queryKey: ['kb-article', article?.slug] });
      navigate(`/kb/${updated.slug}`);
    },
    onError: (err: any) => toast.error(err?.response?.data?.message ?? 'Failed to update article'),
  });

  if (!can('create', 'knowledgeBase')) {
    return (
      <div className="flex flex-col gap-4 pb-10">
        <div className="py-12 text-center">
          <p className="text-err">You don't have permission to manage knowledge base articles.</p>
          <Link to="/kb" className="btn btn-secondary mt-4">Back to Knowledge Base</Link>
        </div>
      </div>
    );
  }

  if (isEditing && loadingArticle) {
    return (
      <div className="flex flex-col gap-4 pb-10">
        <div className="py-12 text-center text-muted">Loading article...</div>
      </div>
    );
  }

  const isPending = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    if (isEditing) {
      updateMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  return (
    <div className="flex flex-col gap-4 pb-10">
      <PageHeader
        title={isEditing ? 'Edit Article' : 'New Article'}
        intro="Write the article content, then set its category, tags, status, and visibility before saving."
      />

      <form onSubmit={handleSubmit} className="max-w-4xl space-y-6">
        <div className="card p-6 space-y-5">
          {/* Title */}
          <div>
            <label className="label">Title *</label>
            <input
              type="text"
              className="input"
              placeholder="Article title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          {/* Excerpt */}
          <div>
            <label className="label">Excerpt <span className="text-muted font-normal">(short summary)</span></label>
            <input
              type="text"
              className="input"
              placeholder="Brief description shown in article listings"
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
            />
          </div>

          {/* Content */}
          <div>
            <label className="label">Content *</label>
            <textarea
              className="input font-mono text-sm leading-relaxed resize-y"
              rows={20}
              placeholder="Write your article content here..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              required
            />
          </div>
        </div>

        <div className="card p-6 space-y-5">
          <h2 className="section-title">Settings</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Category */}
            <div>
              <label className="label">Category</label>
              <input
                type="text"
                className="input"
                placeholder="e.g. Getting Started"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              />
            </div>

            {/* Tags */}
            <div>
              <label className="label">Tags <span className="text-muted font-normal">(comma-separated)</span></label>
              <input
                type="text"
                className="input"
                placeholder="billing, account, setup"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Status */}
            <div>
              <label className="label">Status</label>
              <div className="flex gap-2">
                {(['draft', 'published', 'archived'] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setStatus(s)}
                    className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors capitalize ${
                      status === s
                        ? 'border-primary bg-primary-muted text-primary'
                        : 'border-line text-muted hover:border-primary'
                    }`}
                  >
                    {s === 'published' ? <Eye className="w-3.5 h-3.5 inline mr-1" /> : <EyeOff className="w-3.5 h-3.5 inline mr-1" />}
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Visibility */}
            <div>
              <label className="label">Visibility</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setVisibility('external')}
                  className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                    visibility === 'external'
                      ? 'border-primary bg-primary-muted text-primary'
                      : 'border-line text-muted hover:border-primary'
                  }`}
                >
                  <Globe className="w-3.5 h-3.5 inline mr-1" />
                  Public
                </button>
                <button
                  type="button"
                  onClick={() => setVisibility('internal')}
                  className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                    visibility === 'internal'
                      ? 'border-warn bg-warn/10 text-warn'
                      : 'border-line text-muted hover:border-primary'
                  }`}
                >
                  <Lock className="w-3.5 h-3.5 inline mr-1" />
                  Internal
                </button>
              </div>
              <p className="mt-1.5 text-xs text-muted">
                {visibility === 'internal'
                  ? 'Only visible to staff members (owner, admin, technician)'
                  : 'Visible to all users including customers'}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3">
          <Link to="/kb" className="btn btn-secondary">Cancel</Link>
          <button type="submit" disabled={isPending} className="btn btn-primary inline-flex items-center gap-2">
            <Save className="w-4 h-4" />
            {isPending ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Article'}
          </button>
        </div>
      </form>
    </div>
  );
}
